import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Day } from '../../../stores/TimetableVersion/TimeAvailabilityStore';
import { dayToTranslation } from '../../../utils/dayToTranslation';
import { PreviewSectionContext } from '../../../utils/previewMapSections';
import { useTimetableStore } from '../TimetableProvider';
import styles from './PreviewMap.module.scss';
import { PreviewRow } from './previewMapRows';

type Segment = { top: string; height: string; color?: string };

export type PreviewSectionProps = {
  context: PreviewSectionContext;
  rows: PreviewRow[];
  /** Ressourcen der Karte in der Hand in diesem Kontext → Zeilen-Highlight. */
  pickedResources: Set<string>;
  /** Exakte Slots der Karte in der Hand, `${hourUuid}:${day}:${rowUuid}`. */
  pickedCells: Set<string>;
  /** Nachbarstunden-Markierung, `${hourUuid}:${day}:${rowUuid}` → davor oder danach. */
  markedCells: Map<string, 'previous' | 'next'>;
  /** Textzeilen unter der Überschrift, z. B. „Davor in: R12“ und „Danach in: R7“. */
  notes: string[];
  /** Nur im Mehr-Abschnitt-Modus gesetzt; ohne Titel bleibt das Raster wie bisher. */
  title?: string;
  hours: { value: string; label: string }[];
};

/**
 * Ein Abschnitt der Vorschau-Karte (Klassen-, Lehrenden- oder Räume-Zeilen). Rendert bewusst nur
 * Grid-Kinder in einem Fragment, kein eigenes Raster: alle Abschnitte teilen sich EIN CSS-Grid,
 * damit die Stunden-Spalten über die Abschnitte hinweg exakt untereinander stehen.
 */
export const PreviewSection: FC<PreviewSectionProps> = observer(
  ({ context, rows, pickedResources, pickedCells, markedCells, notes, title, hours }) => {
    const store = useTimetableStore();
    const { t } = useTranslation();

    // Fokus auf die gepickte Karte: kennen wir ihre exakte Position, werden die übrigen belegten
    // Zellen abgedunkelt, damit genau diese Karte heraussticht.
    const focusActive = pickedCells.size > 0;
    const colorBySubject = store.cardColorBySubject;

    // Farbsegmente wie im Hauptboard. Der `cssGradientString` je ClassGridCard trägt bereits die
    // schräge Mehrfarbigkeit bei mehreren Lehrkräften; Teilungen sind eigene ClassGridCards, die
    // per `cssTop`/`cssHeight` gestapelt werden. Genau diese Auswahl (je Kontext) wird hier
    // nachgebildet, statt pauschal die erste Karte zu nehmen. Bei „Fachfarbe für Kärtchen" gewinnt
    // die Fachfarbe — die Position (Teilung) bleibt aber erhalten.
    const cellSegments = (
      rowUuid: string,
      day: Day,
      hourUuid: string,
    ): { segments: Segment[]; count: number; label: string } => {
      const cardUuids = store.cardsBySlotAndResource.get(`${hourUuid}:${day}:${rowUuid}`);
      if (!cardUuids || cardUuids.size === 0) return { segments: [], count: 0, label: '' };

      const segments: Segment[] = [];
      const labels: string[] = [];
      let count = 0;
      cardUuids.forEach((uuid) => {
        const card = store.getCardByUuid(uuid);
        if (!card) return;
        count += 1;
        const gridCards = store.gridCards.get(uuid) ?? [];
        // Klassen-Kontext: alle Teilungen dieser Klasse (mehrere ClassGridCards je classUuid).
        // Lehrer/Raum-Kontext: die eine Karte, über die volle Höhe (Räume) bzw. mit ihrer Höhe.
        // Klassenübergreifende Karten stehen im Index unter jeder Klasse; fehlt für die Zeile ein
        // eigenes ClassGridCard, greift der Fallback, damit die Karte trotzdem sichtbar bleibt.
        const relevant =
          context === 'classes'
            ? gridCards.filter((cc) => cc.classUuid === rowUuid)
            : gridCards.filter((cc) => cc.timetableCardUuid === uuid).slice(0, 1);
        const chosen = relevant.length > 0 ? relevant : gridCards.slice(0, 1);
        if (chosen.length === 0) {
          // Karte ohne ClassGridCard (z.B. reine Funktions-/klassenübergreifende Karte): neutral füllen.
          segments.push({ top: '0%', height: '100%', color: undefined });
        }
        chosen.forEach((cc) => {
          segments.push({
            top: context === 'rooms' ? '0%' : (cc.cssTop ?? '0%'),
            height: context === 'rooms' ? '100%' : (cc.cssHeight ?? '100%'),
            color: colorBySubject
              ? (card.subject?.timetableConfig?.color ?? undefined)
              : (cc.cssGradientString ?? undefined),
          });
        });

        const teachers = card.teachers.map((teacher) => teacher.shortName).join(', ');
        const classes = card.classes.map((schoolClass) => schoolClass.shortName).join(', ');
        const rooms = card.rooms.map((room) => room.name).join(', ');
        const label = [card.subject?.shortName, classes, teachers, rooms].filter(Boolean).join(' · ');
        if (label) labels.push(label);
      });

      return { segments, count, label: labels.join(' | ') };
    };

    return (
      <>
        {title && <div className={styles['section-title']}>{title}</div>}
        {notes.map((note) => (
          <div key={note} className={styles['section-note']}>
            {note}
          </div>
        ))}
        {rows.map((row) => (
          <Fragment key={`${context}-${row.uuid}`}>
            <div
              className={classNames(styles['row-name'], { [styles['picked-row']]: pickedResources.has(row.uuid) })}
              title={row.name}
            >
              {row.name}
            </div>
            {store.days.map((day) =>
              hours.map((hour, hourIndex) => {
                const { segments, count, label } = cellSegments(row.uuid, day.value, hour.value);
                const occupied = count > 0;
                const cellKey = `${hour.value}:${day.value}:${row.uuid}`;
                const isPickedCell = pickedCells.has(cellKey);
                const marker = markedCells.get(cellKey);
                return (
                  <div
                    key={`${row.uuid}-${day.value}-${hour.value}`}
                    className={classNames(styles.cell, {
                      [styles['day-start']]: hourIndex === 0,
                      [styles.occupied]: occupied,
                      [styles['picked-row']]: pickedResources.has(row.uuid),
                      [styles['picked-cell']]: isPickedCell,
                      [styles.adjacent]: Boolean(marker) && !isPickedCell,
                      // Fokus auf die gepickte Karte: alle anderen belegten Zellen abdunkeln.
                      // Nachbarstunden bleiben hell — sie sind hier genau die Information.
                      [styles.dimmed]: focusActive && occupied && !isPickedCell && !marker,
                    })}
                    data-cards={
                      occupied ? `${row.name} — ${t(dayToTranslation(day.value))} ${hour.label}: ${label}` : undefined
                    }
                  >
                    {segments.map((segment, index) => (
                      <span
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        className={styles.segment}
                        style={{ top: segment.top, height: segment.height, background: segment.color }}
                      />
                    ))}
                  </div>
                );
              }),
            )}
          </Fragment>
        ))}
      </>
    );
  },
);

PreviewSection.displayName = 'PreviewSection';
